import { api } from './client';

export type PixStatus = 'pending' | 'paid' | 'expired' | 'cancelled';

export interface CreatePixIn {
  amount_brl: string;
}

export interface PixChargeOut {
  id: string;
  amount_brl: string;
  qr_code_text: string;
  qr_code_image_url: string | null;
  expires_at: string;
  status: PixStatus;
  created_at: string;
}

export interface DevSimulatePaymentResult {
  status: 'paid' | 'already_paid';
  charge_id: string;
  credited_brl?: string;
  cashback_brl?: string;
  cashback_rule_version?: number | null;
  wallet?: { balance_brl: string; reserved_brl: string };
}

// ── Cartão de crédito (fatia 3) ──────────────────────────────────────────

export interface PaymentMethodOut {
  method: 'pix' | 'credit_card';
  enabled: boolean;
  min_brl: string;
  max_brl: string;
  installments_max: number;
}

export interface CreateCardTopupIn {
  amount_brl: string;
  encrypted_card?: string;
  saved_card_id?: string;
  security_code?: string;
  store_card?: boolean;
  three_ds_id?: string;
  recaptcha_token?: string;
}

export interface CardPublicKeyOut {
  public_key: string;
  // Presente só quando o reCAPTCHA v3 está ativo no backend; null = desligado.
  recaptcha_site_key?: string | null;
}

export interface CardTopupOut {
  charge_id: string;
  // paid | authorized | declined | in_analysis | created
  status: string;
  credited_brl: string;
  cashback_brl: string;
  decline_message?: string | null;
  three_ds_status?: string | null;
}

export interface CardChargeStatusOut {
  id: string;
  status: string;
  amount_brl: string;
  captured_brl: string;
  decline_message?: string | null;
  created_at: string;
}

export const paymentsApi = {
  createPix: (amountBrl: number) =>
    api.post<PixChargeOut>('/payments/pix', { amount_brl: amountBrl.toFixed(2) }),
  /**
   * Dev-only: força pagamento de um PIX sem passar pelo PagBank.
   * Backend retorna 404 em production (gated por `settings.env`).
   * Chamar só quando `import.meta.env.VITE_SHOW_DEV_TOOLS` estiver ativo.
   */
  devSimulatePayment: (chargeId: string) =>
    api.post<DevSimulatePaymentResult>(
      `/_dev/pix/${chargeId}/simulate-payment`,
      {}
    ),

  /** Trilhos de pagamento disponíveis. `credit_card.enabled` decide se o tile
   *  'Cartão' é selecionável — sem precisar republicar bundle. */
  getMethods: () => api.get<PaymentMethodOut[]>('/payments/methods'),

  /** Chave pública RSA pra criptografar o cartão no browser (SDK do PagBank).
   *  Pode trazer o site_key do reCAPTCHA v3 quando o recurso está ativo. */
  getCardPublicKey: () =>
    api.get<CardPublicKeyOut>('/payments/card/public-key'),

  /** Aporte via cartão. `encrypted_card` vem do SDK (o PAN nunca sai daqui). */
  createCard: (body: CreateCardTopupIn) =>
    api.post<CardTopupOut>('/payments/card', body),

  /** Polling de status (in_analysis / 3DS pendente). */
  getCardCharge: (chargeId: string) =>
    api.get<CardChargeStatusOut>(`/payments/card/${chargeId}`),

  /** Dev-only: materializa um PAID sintético (IN_ANALYSIS não é forçável no
   *  sandbox ORDER). 404 em produção. */
  devSimulateCardPayment: (chargeId: string) =>
    api.post<{ status: string; charge_id: string; credited_brl: string; cashback_brl: string }>(
      `/_dev/card/${chargeId}/simulate-payment`,
      {}
    )
};
